import React from 'react'

import { makeStyles, useTheme, useMediaQuery, Grid, FilePreview, Typography, HtmlContent } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { useEstimatorQuery } from '@paintscout/api'
import type { PresentationSectionProps } from '../../'
import compareSectionProps from '../../util/compareSectionProps'
import { useQuote } from '../../../context'
import AboutEstimatorPlaceholder from './AboutEstimatorPlaceholder'
import isEmpty from './AboutEstimatorIsEmpty'

const useStyles = makeStyles(
  (theme: Theme) => {
    return {
      root: {},
      content: {},
      image: {
        '& img': {
          borderRadius: theme.borderRadius.md
        }
      },
      signature: {
        maxHeight: 50,
        width: 'auto'
      }
    }
  },
  { name: 'AboutEstimatorSection' }
)

function AboutEstimatorSection(props: PresentationSectionProps) {
  const classes = useStyles(props)
  const { isPreview, isSettings, objectLabels, WrapperComponent, section } = props

  const { estimator, quote } = useQuote()

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const responsiveSpacing = isMobile ? 3 : 6
  const { data: estimatorData } = useEstimatorQuery({
    // skip when context already provides an estimator (always the case in customer-view,
    // where the view graphql schema doesn't expose this query)
    skip: !quote?.owner?.id || !!estimator,
    fetchPolicy: 'cache-and-network',
    variables: { id: quote?.owner?.id, quoteId: quote?._id }
  })
  const fetchedEstimator = estimatorData?.estimator

  const empty = isEmpty({ section, isSettings, estimator })

  let fields = {
    name: '',
    title: '',
    description: '',
    signature: '',
    image: null
  }

  // Ported v1 precedence, kept deliberately: a context estimator only counts when it has a
  // real description or an image; otherwise (or with no context estimator at all) the
  // fetched profile wins wholesale. Partial merging of the two sources is intentionally avoided.
  if ((estimator?.description?.length > 1 && estimator.description !== '<p></p><p></p>') || estimator?.image) {
    fields = {
      name: estimator?.name ?? '',
      title: estimator?.title ?? quote?.owner?.title ?? '',
      description: estimator?.description ?? '',
      signature: estimator?.signature ?? '',
      image: estimator?.image ?? null
    }
  }
  if (!estimator) {
    fields = {
      name: fetchedEstimator?.name ?? '',
      title: fetchedEstimator?.title ?? quote?.owner?.title ?? '',
      description: fetchedEstimator?.description ?? '',
      signature: fetchedEstimator?.signature ?? '',
      image: fetchedEstimator?.image ?? null
    }
  }

  // In the presentation editor there is no quote/estimator context; always show the placeholder
  if (isSettings || (empty && isPreview && quote?._id === 'preview')) {
    return (
      <WrapperComponent {...props}>
        <AboutEstimatorPlaceholder title={section?.title} objectLabels={objectLabels} />
      </WrapperComponent>
    )
  }

  return (
    <WrapperComponent {...props}>
      <Grid container spacing={responsiveSpacing} alignItems={'center'}>
        {section?.title && (
          <Grid item md={12}>
            <Typography variant={'h2'}>{section.title}</Typography>
          </Grid>
        )}
        <Grid item md={12}>
          <Grid container spacing={3}>
            {fields?.image && (
              <Grid item md={6}>
                <FilePreview file={fields.image} classes={{ root: classes.image }} />
              </Grid>
            )}
            {fields?.description && (
              <Grid item md={fields?.image ? 6 : 12}>
                <Grid container spacing={3}>
                  {fields?.name && (
                    <Grid item md={12}>
                      <Typography variant={'h3'}>{fields.name}</Typography>
                      {fields?.title && <Typography variant={'h4'}>{fields.title}</Typography>}
                    </Grid>
                  )}
                  <Grid item md={12}>
                    <HtmlContent content={fields.description} classes={{ root: classes.content }} />
                  </Grid>
                  {fields?.signature && (
                    <Grid item md={12}>
                      <img
                        src={fields.signature}
                        alt={`${fields.name || 'Estimator'} signature`}
                        className={classes.signature}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    </WrapperComponent>
  )
}

export default React.memo(AboutEstimatorSection, (prevProps, nextProps) => {
  return compareSectionProps(prevProps, nextProps)
})
